import React, {Component} from 'react';
import {connect} from 'react-redux';
import {throttle} from 'lodash';

import {Button} from 'primereact/button';
import {OverlayPanel} from 'primereact/overlaypanel';

import ReferenceSetDropdown from '~/frontend/src/components/base/shared/field/ReferenceSetDropdown';
import StatusDropdown from '~/frontend/src/components/base/shared/field/StatusDropdown';
import SearchItemDropdown from '~/frontend/src/components/base/shared/field/SearchItemDropdown';

import metaModel from '~/shared/src/utilities/metaModel';
import {__} from '~/shared/src/utilities/messageCatalog';
import User from '~/shared/src/model/User';
import Item from '~/shared/src/ci/Item';
import {ReferenceOnlyAttributesForCreation} from '~/shared/src/ci/Reference';

import xhr from '~/frontend/src/utilities/xhr';
import {XhrComponentState} from '~/frontend/src/reducers/xhr';
import {StoreState} from '~/frontend/src/store';
import {
    CheckReferenceInsertableParameters,
    CheckReferenceInsertableResult
} from '~/shared/src/utilities/modules/data';
import LocalizableError from '~/shared/src/base/LocalizableError';


type TargetIs = 'src' | 'dest' | 'both';

const defaultProps = {

    /**
     * The ReferenceTypeName of the Reference to add.
     */
    referenceTypeName: '' as string,

    /**
     * The behaviour of the target Item to add the Reference for.
     */
    targetItemIs: 'both' as TargetIs,

    /**
     * The ItemTypeName of the target Item to add the Reference for.
     */
    targetItemTypeName: '' as string,

    /**
     * Should the add button be disabled.
     */
    disabled: true as boolean
};

type DefaultProps = typeof defaultProps;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext to access global.data with.
     */
    dataContext : string;

    /**
     * The database schema of the Reference to add.
     */
    schema : Schema;

    /**
     * The id of the Item to add the Reference for.
     */
    itemId : number;

    /**
     * The ItemTypeName of the Item to add the Reference for.
     */
    itemTypeName : string;

    /**
     * Should the MetaModel information shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode : boolean;

    /**
     * Should the MetaModel information as defined additionally shown for processed information <strong>or</strong> not.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames : boolean;

    /**
     * Callback to invoke if addButton was clicked.
     *
     * @param {ReferenceAttributesForCreation} reference
     * @param {Item} targetItem
     */
    onAddReference : ( reference : ReferenceOnlyAttributesForCreation, targetItem : Item ) => void;

    /**
     * Callback to invoke if the reference is checked.
     *
     * @param {ReferenceAttributesForCreation} reference
     */
    onCheckReference? : ( reference : ReferenceOnlyAttributesForCreation ) => { creatable : boolean, reason? : string };

    /**
     * The CSS class name(s) to add to the component.
     */
    className? : string;
};

type StateProps = {

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps;

type State = {
    componentUid : string;
    referenceTypeName : string;
    itemTypeLabel? : string;
    targetItemIs : TargetIs;
    targetItemTypeName : string;
    referenceSetId : number;
    referenceStatusId : number;
    targetItem : Item | undefined;
    targetItemStatusId : number;
    creatable : boolean;
    referenceError : string;
};

/**
 * AddReferenceDialog
 */
class AddReferenceDialog extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    overlayPanel? : OverlayPanel | null;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = this.getInitialState();

        this.onReferenceSetDropdownChange = this.onReferenceSetDropdownChange.bind( this );
        this.onReferenceStatusDropdownChange = this.onReferenceStatusDropdownChange.bind( this );
        this.onSearchItemDropdownChange = this.onSearchItemDropdownChange.bind( this );
        this.onTargetItemStatusDropdownChange = this.onTargetItemStatusDropdownChange.bind( this );
        this.onCreateButtonClick = this.onCreateButtonClick.bind( this );
        this.onCancelButtonClick = this.onCancelButtonClick.bind( this );
        this.onOverlayPanelHide = this.onOverlayPanelHide.bind( this );

        this.checkReference = throttle( this.checkReference, 200, { trailing: true } );
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : AllProps, prevState : State ) {
        if ( prevState.referenceSetId !== this.state.referenceSetId || prevState.targetItem !== this.state.targetItem ) {
            this.checkReference();
        }
    }

    getInitialState( props : AllProps = this.props ) {
        return {
            componentUid      : `${props.uid}_addReferenceDialog`,
            referenceTypeName : props.referenceTypeName,
            itemTypeLabel     : metaModel[ props.schema ].getItemType( props.itemTypeName )?.label,
            targetItemIs      : props.targetItemIs,
            targetItemTypeName: props.targetItemTypeName,
            referenceSetId    : 0,
            referenceStatusId : metaModel[ props.schema ].getStatus( 'active' )!.id,
            targetItem        : undefined,
            targetItemStatusId: metaModel[ props.schema ].getStatus( 'active' )!.id,
            creatable         : false,
            referenceError    : ''
        };
    }

    /**
     * Handler for ReferenceSetDropdown change
     *
     * @private
     */
    onReferenceSetDropdownChange( referenceSetId : number ) {
        this.setState( {
            referenceSetId,
            creatable: false
        } );
    }

    /**
     * Handler for ReferenceStatusDropdown change
     *
     * @private
     */
    onReferenceStatusDropdownChange( status : string ) {
        this.setState( { referenceStatusId: metaModel[ this.props.schema ].getStatus( status )!.id } );
    }

    /**
     * Handler for SearchItemDropdown change
     *
     * @private
     */
    onSearchItemDropdownChange( item : Item ) {
        this.setState( {
            targetItem: item,
            creatable : false
        } );
    }

    /**
     * Handler for TargetItemStatusDropdown change
     *
     * @private
     */
    onTargetItemStatusDropdownChange( status : string ) {
        this.setState( { targetItemStatusId: metaModel[ this.props.schema ].getStatus( status )!.id } );
    }

    /**
     * Handler for create Button click event
     *
     * @private
     */
    onCreateButtonClick() {
        const reference = this.buildReference(),
              {
                  targetItem,
                  creatable
              }         = this.state;

        this.overlayPanel?.hide();

        if ( reference && targetItem && creatable ) {
            this.props.onAddReference( reference, targetItem );
        }
    }

    /**
     * Handler for cancel Button click event
     *
     * @private
     */
    onCancelButtonClick() {
        this.overlayPanel?.hide();
    }

    /**
     * Handler for OverlayPanel hide event.
     *
     * @private
     */
    onOverlayPanelHide() {
        this.setState( this.getInitialState() );
    }

    /**
     * Validates the chosen reference against database.
     */
    checkReference() {
        const reference  = this.buildReference(),
              requestUid = 'checkReferenceInsertable';

        if ( reference ) {

            // abort running check
            if ( this.props.xhrRequestsForComponent?.[ requestUid ] ) {
                xhr.abort( this.state.componentUid, requestUid );
            }

            // callback
            const onCheckReferenceResult = this.props.onCheckReference?.( reference ) ?? { creatable: true };

            if ( !onCheckReferenceResult.creatable ) {
                this.setState( {
                    creatable     : onCheckReferenceResult.creatable,
                    referenceError: onCheckReferenceResult.reason ?? ''
                } );
            }
            else {
                const doLoad = async () => {
                    const params : CheckReferenceInsertableParameters = {
                              schema               : this.props.schema,
                              referenceSetId       : reference.referenceSetId,
                              srcItemId            : reference.srcItemId,
                              destItemId           : reference.destItemId,
                              referenceIdsToExclude: Object.values( global.data[ this.props.dataContext ].references ).reduce(
                                  ( ids, refToCheck ) => {
                                      if ( refToCheck.referenceSetId === reference.referenceSetId && refToCheck.isDeleted() ) {
                                          ids.push( refToCheck.id );
                                      }
                                      return ids;
                                  },
                                  [] as number[]
                              )
                          },
                          response                                    = await xhr.post(
                              `${BASE_URL}/api/data/checkReferenceInsertable`,
                              params,
                              undefined,
                              this.state.componentUid,
                              requestUid
                          );

                    if ( response?.data ) {
                        const responseData = response.data as CheckReferenceInsertableResult;

                        if ( responseData.success ) {
                            this.setState( {
                                creatable     : responseData.data.insertable,
                                referenceError: responseData.data.reason ? LocalizableError.fromJSON( responseData.data.reason ).toString( false ) : ''
                            } );
                        }
                    }
                };

                doLoad().catch( ( error ) => {
                    console.log( error );
                } );
            }
        }
    }

    /**
     * Builds the reference attributes.
     */
    buildReference() {
        const {
                  schema,
                  itemId,
                  itemTypeName
              } = this.props,
              {
                  referenceStatusId,
                  targetItem,
                  targetItemTypeName
              } = this.state;

        let retVal : ReferenceOnlyAttributesForCreation | undefined,
            referenceSetId      = this.state.referenceSetId,
            srcItemId : number  = 0,
            destItemId : number = 0;

        if ( referenceSetId !== 0 && targetItem ) {

            // circular ReferenceSets (SrcItemType = DestItemType)
            if ( itemTypeName === targetItemTypeName ) {
                if ( referenceSetId > 0 ) {
                    srcItemId = itemId;
                    destItemId = targetItem.id;
                }
                // negative referenceSetId means direction is reversed (targetItem is src)
                else {
                    srcItemId = targetItem.id;
                    destItemId = itemId;
                    referenceSetId = -referenceSetId;
                }
            }

            else {
                const srcItemTypeId = metaModel[ schema ].getReferenceSet( referenceSetId )?.srcItemTypeId,
                      itemTypeId    = metaModel[ schema ].getItemType( itemTypeName )?.id;

                if ( srcItemTypeId && itemTypeId ) {
                    if ( itemTypeId === srcItemTypeId ) {
                        srcItemId = itemId;
                        destItemId = targetItem.id;
                    }
                    else {
                        srcItemId = targetItem.id;
                        destItemId = itemId;
                    }
                }
            }

            retVal = {
                referenceSetId,
                statusId: referenceStatusId,
                srcItemId,
                destItemId
            };
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  schema,
                  itemTypeName,
                  rawMode,
                  showAdditionalRawNames,
                  disabled,
                  className
              } = this.props,
              {
                  referenceTypeName,
                  itemTypeLabel,
                  targetItemIs,
                  targetItemTypeName,
                  referenceSetId,
                  referenceStatusId,
                  targetItem,
                  targetItemStatusId,
                  creatable,
                  referenceError
              } = this.state;

        return <>
            <Button
                onClick={event => this.overlayPanel?.toggle( event, null )}

                disabled={disabled}

                icon="pi pi-plus-circle"
                className={className}
            />
            <OverlayPanel
                ref={el => this.overlayPanel = el}

                appendTo={document.body}
                style={{ width: '60rem' }}
                dismissable={true}
                onHide={this.onOverlayPanelHide}
            >
                <label
                    key={`${uid}_header`}

                    className="yn-menu-title-label"
                >
                    {__( 'YN.base.finder.reference.create' )}
                </label>

                <ReferenceSetDropdown
                    key={`${uid}_referenceSetDropdown`}

                    uid={uid}

                    schema={schema}
                    itemTypeName1={itemTypeName}
                    itemTypeLabel1={`${itemTypeLabel} (${__( 'YN.base.finder.this.label' )})`}
                    itemTypeName2={targetItemTypeName}
                    referenceTypeName={referenceTypeName}
                    referenceDirection={targetItemIs}
                    rawMode={rawMode}
                    showAdditionalRawNames={showAdditionalRawNames}
                    permission="insert"
                    genericPermission="nonBlocked"
                    value={referenceSetId}
                    onChange={this.onReferenceSetDropdownChange}
                    label={__( 'YN.base.finder.reference.setLabel' )}
                    style={{ width: 'calc( 100% - 11rem )', float: 'left' }}
                />
                <StatusDropdown
                    key={`${uid}_referenceSetStatusDropdown`}

                    uid={this.props.uid}

                    value={metaModel[ schema ].getStatus( referenceStatusId )!.name as Status}
                    mode="settable"
                    rawMode={rawMode}
                    showAdditionalRawNames={showAdditionalRawNames}
                    onChange={this.onReferenceStatusDropdownChange}
                    label={__( 'YN.base.finder.reference.statusLabel' )}
                    style={{ width: '10rem', float: 'right' }}
                />

                <SearchItemDropdown
                    key={`${uid}_searchItemDropdown`}

                    uid={this.state.componentUid}

                    schema={schema}
                    status={metaModel[ schema ].getStatus( targetItemStatusId )!.name as Status}
                    itemTypeName={targetItemTypeName}
                    value={targetItem}
                    onChange={this.onSearchItemDropdownChange}
                    label={__( 'YN.base.finder.item.label' )}
                    style={{ width: 'calc( 100% - 11rem)', float: 'left' }}
                />
                <StatusDropdown
                    key={`${uid}_targetItemStatusDropdown`}

                    uid={this.props.uid}

                    value={metaModel[ schema ].getStatus( targetItemStatusId )!.name as Status}
                    mode="settable"
                    rawMode={rawMode}
                    showAdditionalRawNames={showAdditionalRawNames}
                    onChange={this.onTargetItemStatusDropdownChange}
                    label={__( 'YN.base.finder.item.statusLabel' )}
                    style={{ width: '10rem', float: 'right' }}
                />

                {referenceError
                    ? <>
                        <hr className="yn-error-ruler" style={{ clear: 'both' }}/>
                        <span className="yn-error">{referenceError}</span>
                    </>
                    : ''}

                <hr className="mt-2 mb-3 yn-ruler" style={{ clear: 'both' }}/>

                <div className="flex justify-content-end">
                    <Button
                        key={`${uid}_createButton`}

                        label={__( 'YN.base.common.create' )}
                        disabled={!creatable}
                        onClick={this.onCreateButtonClick}
                        className="mr-3 p-button-outlined yn-button-defaultwidth"
                    />
                    <Button
                        key={`${uid}_cancelButton`}

                        label={__( 'YN.base.common.cancel' )}
                        onClick={this.onCancelButtonClick}
                        className="p-button-secondary p-button-outlined yn-button-defaultwidth"
                    />
                </div>
            </OverlayPanel>
        </>;
    }

}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        xhrRequestsForComponent: undefined,
        user                   : undefined
    };

    // get the running xhr request belonging to this component (uid)
    if ( ownProps.uid && state.xhr?.[ ownProps.uid ] ) {
        retVal.xhrRequestsForComponent = state.xhr[ ownProps.uid ];
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

export default connect<StateProps, undefined, ComponentProps, StoreState>( mapStateToProps, undefined )( AddReferenceDialog );
